import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Paper,
  Grid,
  Avatar,
  Chip,
  Divider,
} from '@mui/material';
import { Send, Build, Send as SendIcon } from '@mui/icons-material';
import ProgressIndicator from './ProgressIndicator';
import DiagnosticCard, { DiagnosticStatus } from './DiagnosticCard';

interface DiagnosticResult {
  title: string;
  status: DiagnosticStatus;
  message: string;
  data?: any;
}

interface DiagnosticSummaryProps {
  phoneNumber: string;
  destinationNumber: string;
  ticketId: string;
  diagnosticResults: DiagnosticResult[];
  onRunMessageTest: () => void;
  onTestAnotherNumber: () => void;
}

const DiagnosticSummary: React.FC<DiagnosticSummaryProps> = ({
  phoneNumber,
  destinationNumber,
  ticketId,
  diagnosticResults,
  onRunMessageTest,
  onTestAnotherNumber,
}) => {
  const [isRunningTest, setIsRunningTest] = useState(false);
  const [loadingStates, setLoadingStates] = useState<{ [key: string]: boolean }>({});

  // Initialize loading states for each diagnostic card
  React.useEffect(() => {
    const titles = ['Number Mapping Check', 'SMS Enablement', 'Recent Error Analysis', '10DLC Campaign Status', 'Carrier Approval Status'];
    
    console.log('🔄 Setting up loading states for DiagnosticSummary');
    
    // Set all cards to loading initially
    const initialLoadingStates: { [key: string]: boolean } = {};
    titles.forEach(title => {
      initialLoadingStates[title] = true;
    });
    setLoadingStates(initialLoadingStates);
    console.log('📊 Initial loading states:', initialLoadingStates);

    // Simulate API calls completing one by one
    const simulateApiCalls = async () => {
      for (let i = 0; i < titles.length; i++) {
        console.log(`⏳ Simulating API call for: ${titles[i]}`);
        await new Promise(resolve => setTimeout(resolve, 1500 + Math.random() * 1000));
        setLoadingStates(prev => {
          const newState = { ...prev, [titles[i]]: false };
          console.log(`✅ Completed API call for: ${titles[i]}`, newState);
          return newState;
        });
      }
    };

    // Always simulate loading, even if results exist (for demo purposes)
    simulateApiCalls();
  }, [diagnosticResults.length]); // Re-run when diagnostic results change

  const getIssuesCount = () => {
    return diagnosticResults.filter(result => 
      result.status === 'failed' || result.status === 'warning'
    ).length;
  };

  const getIssuesList = () => {
    const issues: string[] = [];
    
    diagnosticResults.forEach(result => {
      if (result.status === 'failed') {
        issues.push(result.message);
      } else if (result.status === 'warning') {
        issues.push(result.message);
      }
    });
    
    return issues;
  };

  const handleRunMessageTest = async () => {
    setIsRunningTest(true);
    try {
      await onRunMessageTest();
    } finally {
      setIsRunningTest(false);
    }
  };

  const formatPhoneNumber = (number: string) => {
    // Remove any existing formatting and get just digits
    const cleaned = number.replace(/\D/g, '');
    
    // If it's a 10-digit number, format as +1(XXX) XXX-XXXX
    if (cleaned.length === 10) {
      return `+1(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    
    // If it's an 11-digit number starting with 1, format as +1(XXX) XXX-XXXX
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      const withoutCountry = cleaned.slice(1);
      return `+1(${withoutCountry.slice(0, 3)}) ${withoutCountry.slice(3, 6)}-${withoutCountry.slice(6)}`;
    }
    
    // For other cases, return as is
    return number;
  };

  // Check if number mapping exists
  const numberMappingResult = diagnosticResults.find(result => result.title === 'Number Mapping Check');
  const isNumberMapped = numberMappingResult?.status === 'success';
  const isNumberNotFound = numberMappingResult?.data?.errorCode === 404;
  
  // Determine messaging based on number mapping status
  const getMessageTestTitle = () => {
    if (isNumberNotFound) {
      return 'Number Setup Required';
    }
    return 'Next Step: Live Message Test';
  };

  const getMessageTestDescription = () => {
    if (isNumberNotFound) {
      return 'Your phone number needs to be configured in our system before we can send test messages. This helps ensure proper delivery and routing.';
    }
    return 'We can send a test message on your behalf to verify end-to-end delivery and identify any remaining issues.';
  };

  const getMessageTestButtonText = () => {
    if (isNumberNotFound) {
      return isRunningTest ? 'Checking Report...' : 'Check Diagnostic Report';
    }
    return isRunningTest ? 'Running Test...' : 'Run Message Test';
  };

  const getMessageTestButtonColor = () => {
    if (isNumberNotFound) {
      return 'warning';
    }
    return 'success';
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        padding: { xs: 2, md: 4 },
      }}
    >
      <ProgressIndicator currentStep={3} steps={['Input', 'Diagnostics', 'Results', 'Report']} />
      
      <Box sx={{ maxWidth: 1200, margin: '0 auto' }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 3, mt: 2 }}>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              mb: 1,
              fontSize: { xs: '2rem', md: '3rem' },
            }}
          >
            Diagnostic Summary
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: 'text.secondary',
              mb: 1,
              opacity: 0.9,
              fontWeight: 500,
            }}
          >
            Analysis complete for {formatPhoneNumber(phoneNumber.replace('+', ''))}
          </Typography>
          <Chip
            label={`Ticket: ${ticketId}`}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              color: 'text.primary',
              fontWeight: 600,
              fontSize: '0.875rem',
            }}
          />
        </Box>

        {/* Diagnostic Cards */}
        <Box sx={{ mb: 6 }}>
          {diagnosticResults.map((result, index) => (
            <Box key={index} sx={{ mb: 3 }}>
              <DiagnosticCard
                title={result.title}
                status={result.status}
                message={result.message}
                data={result.data}
                isLoading={loadingStates[result.title] || false}
              />
            </Box>
          ))}
        </Box>

        {/* Issues Detected */}
        {getIssuesCount() > 0 && (
          <Paper
            sx={{
              p: 4,
              mb: 6,
              backgroundColor: '#FFF9DB',
              borderRadius: 3,
              border: '2px solid #FFE58F',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Chip
                label={`${getIssuesCount()} Issues Detected`}
                sx={{
                  backgroundColor: '#FFE58F',
                  color: '#000000',
                  fontWeight: 700,
                  fontSize: '1rem',
                  py: 1,
                  px: 2,
                }}
              />
            </Box>
            <Box sx={{ pl: 2 }}>
              {getIssuesList().map((issue, index) => (
                <Box
                  key={index}
                  sx={{
                    backgroundColor: '#FFFFFF',
                    p: 2,
                    mb: 2,
                    borderRadius: 2,
                    border: '1px solid #FFE58F',
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#000000',
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    {issue}
                  </Typography>
                  {/* {index === 0 && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#595959',
                        fontStyle: 'italic',
                      }}
                    >
                      Recommendation: Enable SMS through number management portal
                    </Typography>
                  )} */}
                </Box>
              ))}
            </Box>
          </Paper>
        )}

        {/* Live Message Test Section */}
        <Paper
          sx={{
            p: 4,
            mb: 6,
            backgroundColor: 'rgba(33, 150, 243, 0.1)',
            borderRadius: 3,
            border: '2px solid',
            borderColor: 'primary.main',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: 'primary.main',
              mb: 3,
            }}
          >
            {getMessageTestTitle()}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              mb: 3,
              lineHeight: 1.6,
            }}
          >
            {getMessageTestDescription()}
          </Typography>
          <Box
            sx={{
              p: 3,
              mb: 4,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                fontFamily: 'monospace',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                justifyContent: 'flex-start',
              }}
            >
              <SendIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              Test message: {formatPhoneNumber(phoneNumber.replace('+', ''))} → {formatPhoneNumber(destinationNumber.replace('+', ''))}
            </Typography>
          </Box>
          <Button
            variant="contained"
            color={getMessageTestButtonColor()}
            size="large"
            startIcon={<Send />}
            onClick={handleRunMessageTest}
            disabled={isRunningTest}
            sx={{
              borderRadius: 3,
              py: 2,
              px: 4,
              fontWeight: 700,
              fontSize: '1.1rem',
              textTransform: 'none',
              boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
              '&:hover': {
                boxShadow: '0 6px 16px rgba(76, 175, 80, 0.4)',
                transform: 'translateY(-1px)',
              },
            }}
          >
            {getMessageTestButtonText()}
          </Button>
        </Paper>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center' }}>
          <Button
            variant="outlined"
            onClick={onTestAnotherNumber}
            sx={{
              borderRadius: 3,
              py: 2,
              px: 4,
              fontWeight: 600,
              fontSize: '1rem',
              color: 'text.primary',
              borderColor: 'rgba(0, 0, 0, 0.2)',
              textTransform: 'none',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                transform: 'translateY(-1px)',
              },
            }}
          >
            Test Another Number
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default DiagnosticSummary; 
